import React, { useState } from 'react'
import { FlatList, ScrollView, Text, TouchableOpacity, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Centers } from './components/Centers'
import { NavigationProp, RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { DaySelector } from '../../components/DaySelector'
import { Services } from '../../components/Services/Services'
import { TemplatePage } from '../../components/TemplatePage'
import { useForm } from '../../hooks/useForm'
import { buttonStyle } from '../../theme/buttons'
import { globalStyle } from '../../theme/global'
import { disabledGray } from '../../theme/colors'
import {
  CenterCategoryOneType,
  CenterInfo,
  ControlServicesRoutes,
  ValuesControlService,
} from '../../navigator/types'
import moment from 'moment'
import { useKeyboardVisibility } from '../../hooks/useKeyboardVisibility'
import { useMoveScroll } from '../../hooks/useMoveScroll'
import { Separator } from '../../components/Separator'
import { OptionsItem } from '../../components/OptionsItem'

const CATEGORY_ONE = 1
const MAX_CENTERS_CATEGORY_ONE = 6

interface Navigation extends NavigationProp<ControlServicesRoutes, 'ControlServices'> {}
export const ControlServices = () => {
  const navigation = useNavigation<Navigation>()
  const route = useRoute<RouteProp<ControlServicesRoutes, 'ControlServices'>>()

  const [selectedCenters, setSelectedCenters] = useState<CenterCategoryOneType>({})
  const { t } = useTranslation()

  const { values, onChangeValues: handleSetValues } = useForm<
    Omit<ValuesControlService, 'jobId' | 'choreId' | 'centersCategoryOne'>
  >({
    categoryId: route.params?.categoryId ?? 0,
    date: route.params?.date ?? moment().format('YYYY-MM-DD'),
    centerId: route.params?.centerId ?? 0,
    centerName: route.params?.centerName ?? '',
    namePage: route.params?.namePage ?? '',
    categoryFailure: route.params?.categoryFailure ?? 0,
    boardingName: route.params?.boardingName ?? '',
    serviceId: route.params?.serviceId ?? 0,
  })

  const { handleKeyboardPadding } = useKeyboardVisibility()
  const { scrollViewRef } = useMoveScroll({
    handleKeyboardPadding,
  })

  const handleNavigate = () => {
    const formKey = `${values.serviceId}-${values.date}`

    const centersOrder = Object.values(selectedCenters)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => c.id)

    const firstCenter = centersOrder[0] ?? Number(Object.keys(selectedCenters)[0])

    navigation.navigate('JobsAndChore', {
      ...values,
      formKey,
      currentCenter: firstCenter,
      centersOrder,
      detail: route.params?.detail ?? [],
      failure: route.params?.failure ?? null,
      observation: route.params?.observation ?? '',
      jobId: route.params?.jobId ?? 0,
      choreId: route.params?.choreId ?? 0,
      centersCat1Index: 0,
      centersPercentageTotal: 0,
      centersCategoryOne: route.params?.centersCategoryOne ?? selectedCenters,
      netWashingDetail: route.params?.netWashingDetail ?? [],
      percentage: route.params?.percentage ?? 0,
    })
  }

  const isDisabledButton = (): boolean => {
    if (values.categoryId !== CATEGORY_ONE) {
      return Object.values(values).some(
        (value) => value.toString() === '0' || value.toString() === '',
      )
    }
    return Object.values(selectedCenters).length === 0
  }

  return (
    <TemplatePage
      styleContent={{ padding: 0, marginBottom: 0 }}
      header
      title={`${t('CONTROL SERVICES')}`}
    >
      <View style={[globalStyle.viewContainer, { padding: 24 }]}>
        <ScrollView showsVerticalScrollIndicator={false} ref={scrollViewRef}>
          <Services
            initialValues={{
              boardingName: values.boardingName,
              category: values.categoryId,
              categoryFailure: values.categoryFailure,
              serviceId: values.serviceId,
              serviceName: values.namePage,
            }}
            getServiceId={(value) => {
              handleSetValues({ name: 'serviceId', value: value.serviceId })
              handleSetValues({ name: 'categoryId', value: value.category })
              handleSetValues({ name: 'namePage', value: value.serviceName })
              handleSetValues({ name: 'categoryFailure', value: value.categoryFailure })
              handleSetValues({ name: 'boardingName', value: value.boardingName })
            }}
          />

          <Separator customStyle={{ marginTop: 16 }} />
          {values.serviceId !== 0 && (
            <>
              <DaySelector
                style={{ marginTop: 25 }}
                getDate={(date) => handleSetValues({ name: 'date', value: date })}
                initialValue={values.date}
              />

              <Separator customStyle={{ marginTop: 16 }} />
            </>
          )}

          {values.serviceId !== 0 && (
            <View style={handleKeyboardPadding()}>
              <View>
                <Centers
                  getCenterId={(value) => {
                    if (values.categoryId !== CATEGORY_ONE) {
                      handleSetValues({ name: 'centerId', value: value.id })
                      handleSetValues({ name: 'centerName', value: value.name })
                      return
                    }
                    setSelectedCenters((prev) => {
                      if (prev[value.id]) {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { [value.id]: toDelete, ...rest } = prev
                        return rest
                      }
                      if (Object.keys(prev).length < MAX_CENTERS_CATEGORY_ONE) {
                        return {
                          ...prev,
                          [value.id]: {
                            ...value,
                            percentage: 0,
                            ceco: 'CECO',
                            observation: '',
                            jobId: 0,
                            choreId: 0,
                            shift: 'AM',
                            failureDetail: null,
                            failure: null,
                            jobList: {
                              AM: {},
                              PM: {},
                              'Jornada Completa': {},
                            },
                          } as CenterInfo,
                        }
                      }
                      return prev
                    })
                  }}
                  multiple={values.categoryId === CATEGORY_ONE}
                  initialValues={values.centerId}
                >
                  {values.categoryId === CATEGORY_ONE && (
                    <>
                      <FlatList
                        horizontal
                        data={Object.values(selectedCenters)}
                        renderItem={({ item }) => (
                          <OptionsItem
                            item={item}
                            onPress={(item) => {
                              setSelectedCenters((prev) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const { [item.id]: toDelete, ...rest } = prev
                                return {
                                  ...rest,
                                }
                              })
                            }}
                            isSelected
                            circle
                            closeIcon
                          />
                        )}
                      />
                    </>
                  )}
                </Centers>
              </View>

              <TouchableOpacity
                style={[
                  buttonStyle.basic,
                  { width: '100%', marginBottom: 50, marginTop: 20 },
                  isDisabledButton() && { backgroundColor: disabledGray },
                ]}
                onPress={handleNavigate}
                activeOpacity={0.7}
                disabled={isDisabledButton()}
              >
                <Text style={buttonStyle.text}>Continuar</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>
    </TemplatePage>
  )
}
